import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import EastRoundedIcon from '@mui/icons-material/EastRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import RecyclingRoundedIcon from '@mui/icons-material/RecyclingRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../utils/apiClient';
import Navbar from '../components/Navigation/Navbar';
import './HomeLanding.css';

const COPY = {
  en: {
    eyebrow: 'Tunisia’s marketplace, in motion',
    titleA: 'Find it.',
    titleB: 'Bid for it.',
    titleC: 'Make it yours.',
    heroBody: 'Live auctions, instant shopping and trusted second-hand deals—one beautifully connected marketplace built for Tunisia.',
    liveCta: 'Enter live auctions',
    marketCta: 'Explore marketplace',
    liveNow: 'Live now',
    nextDrop: 'Next auction drop',
    latestWin: 'Latest win',
    wonBy: 'Won by',
    retailPrice: 'Retail price',
    winningPrice: 'Winning bid',
    savedLabel: 'saved',
    noWinYet: 'First win coming soon',
    noWinBody: 'When a room closes, the winning bid shows up right here.',
    justNow: 'just now',
    currentBid: 'Current bid',
    startingAt: 'Starting at',
    participants: 'participants',
    openRoom: 'Open room',
    noRoom: 'Fresh rooms are being prepared',
    noRoomBody: 'Be first in line when the next curated auction goes live.',
    trustEscrow: 'TND escrow protection',
    trustRealtime: 'Realtime bidding',
    trustVerified: 'Verified marketplace',
    marquee: ['LIVE AUCTIONS', 'TECH', 'COLLECTIBLES', 'FASHION', 'HOME', 'SECOND-HAND', 'RARE FINDS'],
    ecosystemKicker: 'One account. Three ways to move.',
    ecosystemTitle: 'A marketplace that meets you wherever the deal begins.',
    ecosystemBody: 'Chase the room, take the instant price, or make an offer directly. Vitria keeps every path connected.',
    auctionTitle: 'Feel the room.',
    auctionBody: 'Enter live, buy your bid pack and compete in real time with transparent room activity.',
    shopTitle: 'Skip the wait.',
    shopBody: 'Discover curated new products and check out with your wallet in a few clean steps.',
    resaleTitle: 'Make the offer.',
    resaleBody: 'Buy and sell pre-loved pieces with fee-backed intent, private chat and seller control.',
    explore: 'Explore',
    liveKicker: 'The pulse of Vitria',
    liveTitle: 'Rooms worth showing up for.',
    liveBody: 'Every auction has a clear clock, a visible lead and one decisive final moment.',
    seeAll: 'See all rooms',
    emptyTitle: 'The floor is quiet—for now.',
    emptyBody: 'New rooms are being curated. Browse the marketplace while the next countdown gets ready.',
    browseShop: 'Browse the shop',
    recoveryKicker: 'A smarter way to keep playing',
    recoveryTitle: 'Lose the auction. Keep the momentum.',
    recoveryBody: 'When eligible auction fees do not win the room, Vitria can return their value to your Recovery Wallet—ready to use on marketplace purchases.',
    recoveryLabel: 'Recovery Wallet',
    recovered: 'Recovered value',
    usableOn: 'Ready for marketplace purchases',
    learnWallet: 'Discover your recovery wallet',
    recoveryValueTitle: 'Recovery value',
    recoveryValueBody: 'Eligible losing auction spend can return as marketplace power.',
    flexibleTitle: 'Flexible checkout',
    flexibleBody: 'Use recovered value alone or alongside your main wallet.',
    visibleTitle: 'Visible history',
    visibleBody: 'Track where value came from and when it is available.',
    marketKicker: 'Curated marketplace',
    marketTitle: 'New, noteworthy, and ready now.',
    marketBody: 'No countdown required. Shop standout products with clear pricing and a direct path to checkout.',
    viewProduct: 'View product',
    noProducts: 'The next collection is being prepared.',
    howKicker: 'Built around the deal',
    howTitle: 'From discovery to done—with less friction.',
    stepOne: 'Discover your lane',
    stepOneBody: 'Browse live rooms, fixed-price products or second-hand listings from one account.',
    stepTwo: 'Move with confidence',
    stepTwoBody: 'Bid, buy or offer with wallet visibility and clear marketplace rules.',
    stepThree: 'Close securely',
    stepThreeBody: 'Protected settlement, notifications and direct contact keep every deal moving.',
    sellerKicker: 'Your next buyer is already here',
    sellerTitle: 'Turn the things you own into the things you want.',
    sellerBody: 'List a product, propose an auction room or open the conversation with serious buyers across Tunisia.',
    sellCta: 'Start selling',
    createCta: 'Create your account',
    finalBadge: 'Vitria / Tunisia',
    finalTitle: 'Good finds move fast.',
    finalBody: 'Join the marketplace built for the thrill of winning and the confidence of a protected deal.',
    joinCta: 'Join Vitria',
  },
  fr: {
    eyebrow: 'La marketplace tunisienne en mouvement',
    titleA: 'Trouvez-le.',
    titleB: 'Misez dessus.',
    titleC: 'Faites-en le vôtre.',
    heroBody: 'Enchères en direct, shopping instantané et bonnes affaires d’occasion—une marketplace connectée, pensée pour la Tunisie.',
    liveCta: 'Entrer dans les enchères',
    marketCta: 'Explorer la marketplace',
    liveNow: 'En direct',
    nextDrop: 'Prochaine enchère',
    latestWin: 'Dernière victoire',
    wonBy: 'Remportée par',
    retailPrice: 'Prix boutique',
    winningPrice: 'Enchère gagnante',
    savedLabel: 'économisé',
    noWinYet: 'Première victoire bientôt',
    noWinBody: 'Dès qu’une salle se termine, l’enchère gagnante s’affiche ici.',
    justNow: 'à l’instant',
    currentBid: 'Enchère actuelle',
    startingAt: 'À partir de',
    participants: 'participants',
    openRoom: 'Ouvrir la salle',
    noRoom: 'De nouvelles salles se préparent',
    noRoomBody: 'Soyez au premier rang dès la prochaine mise en ligne.',
    trustEscrow: 'Protection par entiercement TND',
    trustRealtime: 'Enchères en temps réel',
    trustVerified: 'Marketplace vérifiée',
    marquee: ['ENCHÈRES LIVE', 'TECH', 'COLLECTION', 'MODE', 'MAISON', 'OCCASION', 'PIÈCES RARES'],
    ecosystemKicker: 'Un compte. Trois façons d’avancer.',
    ecosystemTitle: 'La marketplace qui s’adapte à chaque opportunité.',
    ecosystemBody: 'Vivez l’enchère, choisissez le prix direct ou faites une offre. Vitria relie chaque parcours.',
    auctionTitle: 'Vivez la salle.',
    auctionBody: 'Entrez en direct, choisissez votre pack et enchérissez avec une activité totalement visible.',
    shopTitle: 'N’attendez pas.',
    shopBody: 'Découvrez des produits neufs sélectionnés et payez avec votre portefeuille en quelques étapes.',
    resaleTitle: 'Faites votre offre.',
    resaleBody: 'Achetez et vendez d’occasion avec intention vérifiée, chat privé et contrôle vendeur.',
    explore: 'Explorer',
    liveKicker: 'Le pouls de Vitria',
    liveTitle: 'Des salles qui méritent le rendez-vous.',
    liveBody: 'Chaque enchère a un chrono clair, une position visible et un dernier instant décisif.',
    seeAll: 'Voir toutes les salles',
    emptyTitle: 'La salle est calme—pour l’instant.',
    emptyBody: 'De nouvelles enchères sont en préparation. Explorez la boutique pendant ce temps.',
    browseShop: 'Voir la boutique',
    recoveryKicker: 'Une façon plus intelligente de continuer',
    recoveryTitle: 'Perdez l’enchère. Gardez votre élan.',
    recoveryBody: 'Quand certains frais d’enchère ne remportent pas la salle, Vitria peut les convertir en valeur dans votre Recovery Wallet—utilisable sur la marketplace.',
    recoveryLabel: 'Recovery Wallet',
    recovered: 'Valeur récupérée',
    usableOn: 'Disponible pour vos achats marketplace',
    learnWallet: 'Découvrir le Recovery Wallet',
    recoveryValueTitle: 'Valeur récupérée',
    recoveryValueBody: 'Certaines dépenses d’enchères perdues reviennent comme pouvoir d’achat.',
    flexibleTitle: 'Paiement flexible',
    flexibleBody: 'Utilisez cette valeur seule ou avec votre portefeuille principal.',
    visibleTitle: 'Historique transparent',
    visibleBody: 'Suivez l’origine de chaque valeur et sa date de disponibilité.',
    marketKicker: 'Marketplace sélectionnée',
    marketTitle: 'Nouveau, remarquable, disponible maintenant.',
    marketBody: 'Pas de compte à rebours. Découvrez des produits au prix clair, prêts à commander.',
    viewProduct: 'Voir le produit',
    noProducts: 'La prochaine collection est en préparation.',
    howKicker: 'Pensé autour de la transaction',
    howTitle: 'De la découverte à l’accord—sans friction.',
    stepOne: 'Choisissez votre voie',
    stepOneBody: 'Explorez les enchères live, les produits à prix fixe et l’occasion depuis un seul compte.',
    stepTwo: 'Avancez en confiance',
    stepTwoBody: 'Misez, achetez ou proposez avec une vision claire du portefeuille et des règles.',
    stepThree: 'Concluez sereinement',
    stepThreeBody: 'Règlement protégé, notifications et contact direct font avancer chaque transaction.',
    sellerKicker: 'Votre prochain acheteur est déjà ici',
    sellerTitle: 'Transformez ce que vous possédez en ce que vous voulez.',
    sellerBody: 'Publiez un produit, proposez une salle d’enchères ou échangez avec des acheteurs sérieux partout en Tunisie.',
    sellCta: 'Commencer à vendre',
    createCta: 'Créer un compte',
    finalBadge: 'Vitria / Tunisie',
    finalTitle: 'Les bonnes trouvailles partent vite.',
    finalBody: 'Rejoignez la marketplace conçue pour le plaisir de gagner et la confiance d’un accord protégé.',
    joinCta: 'Rejoindre Vitria',
  },
};

const AR_COPY = {
  eyebrow: 'سوق تونس، في حركة',
  titleA: 'اكتشفه.',
  titleB: 'زايد عليه.',
  titleC: 'واجعله لك.',
  heroBody: 'مزادات مباشرة، تسوق فوري وصفقات موثوقة للمنتجات المستعملة—كلها في سوق تونسي واحد مترابط.',
  liveCta: 'ادخل المزادات المباشرة',
  marketCta: 'استكشف السوق',
  liveNow: 'مباشر الآن',
  nextDrop: 'المزاد القادم',
  latestWin: 'آخر فوز',
  wonBy: 'فاز بها',
  retailPrice: 'سعر المتجر',
  winningPrice: 'المزايدة الفائزة',
  savedLabel: 'توفير',
  noWinYet: 'أول فوز قريباً',
  noWinBody: 'عند انتهاء أي قاعة، ستظهر المزايدة الفائزة هنا.',
  justNow: 'الآن',
  currentBid: 'المزايدة الحالية',
  startingAt: 'يبدأ من',
  participants: 'مشاركون',
  openRoom: 'افتح القاعة',
  noRoom: 'يتم تحضير قاعات جديدة',
  noRoomBody: 'كن من الأوائل عند انطلاق المزاد المختار القادم.',
  trustEscrow: 'حماية الدفع بالدينار التونسي',
  trustRealtime: 'مزايدة لحظية',
  trustVerified: 'سوق موثوق',
  marquee: ['مزادات مباشرة', 'تقنية', 'مقتنيات', 'أزياء', 'منزل', 'مستعمل', 'قطع نادرة'],
  ecosystemKicker: 'حساب واحد. ثلاث طرق للتقدم.',
  ecosystemTitle: 'سوق يواكبك أينما بدأت الصفقة.',
  ecosystemBody: 'عش أجواء المزاد، اشتر بالسعر المباشر أو قدم عرضك. Vitria تجمع كل المسارات في تجربة واحدة.',
  auctionTitle: 'عش أجواء القاعة.',
  auctionBody: 'ادخل مباشرة، اختر باقة المزايدات وتنافس لحظة بلحظة مع نشاط واضح للجميع.',
  shopTitle: 'لا تنتظر.',
  shopBody: 'اكتشف منتجات جديدة مختارة وادفع من محفظتك في خطوات بسيطة.',
  resaleTitle: 'قدم عرضك.',
  resaleBody: 'اشتر وبع المنتجات المستعملة بعروض جدية، محادثة خاصة وتحكم كامل للبائع.',
  explore: 'استكشف',
  liveKicker: 'نبض Vitria',
  liveTitle: 'قاعات تستحق الموعد.',
  liveBody: 'لكل مزاد توقيت واضح، متصدر ظاهر ولحظة أخيرة حاسمة.',
  seeAll: 'شاهد كل القاعات',
  emptyTitle: 'القاعة هادئة—مؤقتًا.',
  emptyBody: 'يجري إعداد مزادات جديدة. تصفح المتجر إلى حين بدء العد التنازلي القادم.',
  browseShop: 'تصفح المتجر',
  recoveryKicker: 'طريقة أذكى لمواصلة التقدم',
  recoveryTitle: 'اخسر المزاد. واحتفظ بقيمتك.',
  recoveryBody: 'عندما لا تقودك بعض رسوم المزايدة للفوز، يمكن لـ Vitria إعادتها كقيمة في محفظة الاسترجاع لاستخدامها في السوق.',
  recoveryLabel: 'محفظة الاسترجاع',
  recovered: 'القيمة المسترجعة',
  usableOn: 'جاهزة لمشتريات السوق',
  learnWallet: 'اكتشف محفظة الاسترجاع',
  recoveryValueTitle: 'قيمة مسترجعة',
  recoveryValueBody: 'قد تعود مصاريف المزايدات المؤهلة كقوة شرائية في السوق.',
  flexibleTitle: 'دفع مرن',
  flexibleBody: 'استخدم القيمة المسترجعة وحدها أو مع محفظتك الرئيسية.',
  visibleTitle: 'سجل واضح',
  visibleBody: 'تابع مصدر كل قيمة وموعد توفرها.',
  marketKicker: 'منتجات مختارة',
  marketTitle: 'جديد، مميز ومتاح الآن.',
  marketBody: 'لا حاجة لانتظار العد التنازلي. تسوق منتجات بأسعار واضحة ومسار مباشر للدفع.',
  viewProduct: 'عرض المنتج',
  noProducts: 'يتم تحضير المجموعة القادمة.',
  howKicker: 'مصمم حول الصفقة',
  howTitle: 'من الاكتشاف إلى الاتفاق—بخطوات أقل.',
  stepOne: 'اختر مسارك',
  stepOneBody: 'تصفح المزادات المباشرة، المنتجات ذات السعر الثابت أو عروض المستعمل من حساب واحد.',
  stepTwo: 'تقدم بثقة',
  stepTwoBody: 'زايد، اشتر أو قدم عرضًا مع رؤية واضحة للمحفظة وقواعد السوق.',
  stepThree: 'أتمم الصفقة بأمان',
  stepThreeBody: 'دفع محمي، إشعارات وتواصل مباشر تدفع كل صفقة إلى الأمام.',
  sellerKicker: 'مشتريك القادم موجود هنا',
  sellerTitle: 'حوّل ما تملكه إلى ما تريده.',
  sellerBody: 'انشر منتجًا، اقترح قاعة مزاد أو تواصل مع مشترين جادين في كامل تونس.',
  sellCta: 'ابدأ البيع',
  createCta: 'أنشئ حسابك',
  finalBadge: 'Vitria / تونس',
  finalTitle: 'الفرص الجيدة لا تنتظر.',
  finalBody: 'انضم إلى السوق المصمم لمتعة الفوز والثقة في صفقة محمية.',
  joinCta: 'انضم إلى Vitria',
};

const LANDING_META = {
  en: {
    locale: 'en-TN', direction: 'ltr', scroll: 'Scroll', marketplaceAria: 'Marketplace categories',
    curatedSelection: 'Vitria select', liveSelection: 'Live selection',
    placeholderCategories: ['Tech', 'Home', 'Style', 'Collectibles'], brandTagline: 'Bid. Buy. Belong.',
    footerMarketplace: 'Marketplace', footerLiveAuctions: 'Live auctions', footerShop: 'Shop', footerSecondHand: 'Second-hand',
    footerAccount: 'Account', footerProfile: 'Profile', footerHistory: 'History', footerSettings: 'Settings',
    footerRegion: 'Vitria Tunisia', footerLocation: 'Tunis, Tunisia', footerCopyright: 'Vitria. All rights reserved.',
    categoryLabels: { 'Electronics & Tech': 'Electronics & Tech', Entertainment: 'Entertainment', 'Fashion & Beauty': 'Fashion & Beauty', 'Home & Garden': 'Home & Garden' },
  },
  fr: {
    locale: 'fr-TN', direction: 'ltr', scroll: 'Défiler', marketplaceAria: 'Catégories de la marketplace',
    curatedSelection: 'Sélection Vitria', liveSelection: 'Sélection live',
    placeholderCategories: ['Tech', 'Maison', 'Style', 'Collection'], brandTagline: 'Misez. Achetez. Appartenez.',
    footerMarketplace: 'Marketplace', footerLiveAuctions: 'Enchères live', footerShop: 'Boutique', footerSecondHand: 'Occasion',
    footerAccount: 'Compte', footerProfile: 'Profil', footerHistory: 'Historique', footerSettings: 'Paramètres',
    footerRegion: 'Vitria Tunisie', footerLocation: 'Tunis, Tunisie', footerCopyright: 'Vitria. Tous droits réservés.',
    categoryLabels: { 'Electronics & Tech': 'Électronique & Tech', Entertainment: 'Divertissement', 'Fashion & Beauty': 'Mode & Beauté', 'Home & Garden': 'Maison & Jardin' },
  },
  ar: {
    locale: 'ar-TN', direction: 'rtl', scroll: 'مرّر', marketplaceAria: 'فئات السوق',
    curatedSelection: 'اختيار Vitria', liveSelection: 'اختيار مباشر',
    placeholderCategories: ['تقنية', 'منزل', 'أناقة', 'مقتنيات'], brandTagline: 'زايد. اشترِ. وانتمِ.',
    footerMarketplace: 'السوق', footerLiveAuctions: 'المزادات المباشرة', footerShop: 'المتجر', footerSecondHand: 'المستعمل',
    footerAccount: 'الحساب', footerProfile: 'الملف الشخصي', footerHistory: 'السجل', footerSettings: 'الإعدادات',
    footerRegion: 'Vitria تونس', footerLocation: 'تونس، الجمهورية التونسية', footerCopyright: 'Vitria. جميع الحقوق محفوظة.',
    categoryLabels: { 'Electronics & Tech': 'الإلكترونيات والتقنية', Entertainment: 'الترفيه', 'Fashion & Beauty': 'الأزياء والجمال', 'Home & Garden': 'المنزل والحديقة' },
  },
};

const money = (value, locale = 'en-TN') => `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Number(value) || 0)} TND`;

const translateCategory = (category, copy, fallback) => copy.categoryLabels?.[category] || category || fallback;

const getImage = (item) => {
  const image = item?.productId?.images?.[0] || item?.images?.[0];
  if (typeof image === 'string') return image;
  return image?.thumbnailUrl || image?.url || null;
};

const timeAgo = (date, now, copy) => {
  if (!date) return '--';
  const diff = Math.max(0, now - new Date(date).getTime());
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return copy.justNow;
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
};

const getCountdown = (date, now) => {
  if (!date) return '-- : -- : --';
  const delta = Math.max(0, new Date(date).getTime() - now);
  const hours = Math.floor(delta / 3600000);
  const minutes = Math.floor((delta % 3600000) / 60000);
  const seconds = Math.floor((delta % 60000) / 1000);
  return [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(' : ');
};

const ArrowLink = ({ to, children, light = false }) => (
  <RouterLink className={`vl-arrow-link${light ? ' is-light' : ''}`} to={to}>
    <span>{children}</span>
    <ArrowOutwardRoundedIcon />
  </RouterLink>
);

const ProductTile = ({ product, copy, index }) => {
  const image = getImage(product);
  return (
    <article className={`vl-product-tile tone-${index % 4}`}>
      <div className="vl-product-media">
        {image ? <img src={image} alt={product?.name || ''} /> : <Inventory2RoundedIcon />}
        <span className="vl-product-category">{translateCategory(product?.category, copy, copy.curatedSelection)}</span>
      </div>
      <div className="vl-product-copy">
        <h3>{product?.name || copy.noProducts}</h3>
        <div className="vl-product-bottom">
          <strong>{money(product?.price, copy.locale)}</strong>
          {product?._id && (
            <RouterLink to={`/ecommerce/product/${product._id}`} aria-label={`${copy.viewProduct}: ${product.name}`}>
              <EastRoundedIcon />
            </RouterLink>
          )}
        </div>
      </div>
    </article>
  );
};

const AuctionTile = ({ room, copy, now }) => {
  const product = room?.productId || {};
  const image = getImage(room);
  const bidders = room?.participants?.length || room?.analytics?.participantCount || 0;
  return (
    <article className="vl-auction-tile">
      <div className="vl-auction-image">
        {image ? <img src={image} alt={product.name || room?.title || ''} /> : <GavelRoundedIcon />}
        <span className="vl-live-pill"><i /> {copy.liveNow}</span>
        <span className="vl-countdown">{getCountdown(room?.endTime, now)}</span>
      </div>
      <div className="vl-auction-copy">
        <span>{translateCategory(product?.category, copy, copy.liveSelection)}</span>
        <h3>{product?.name || room?.title || copy.noRoom}</h3>
        <div className="vl-auction-meta">
          <div><small>{copy.currentBid}</small><strong>{money(room?.currentHighestBid?.amount || room?.minBid, copy.locale)}</strong></div>
          <div className="vl-bidder-count"><span>{bidders}</span> {copy.participants}</div>
        </div>
        <ArrowLink to={`/auction-room/${room?._id}`}>{copy.openRoom}</ArrowLink>
      </div>
    </article>
  );
};

const Home = () => {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const theme = useTheme();
  const language = String(i18n.language || 'en').toLowerCase();
  const languageKey = language.startsWith('ar') ? 'ar' : language.startsWith('fr') ? 'fr' : 'en';
  const copy = useMemo(() => ({
    ...(languageKey === 'ar' ? AR_COPY : COPY[languageKey]),
    ...LANDING_META[languageKey],
  }), [languageKey]);
  const [now, setNow] = useState(Date.now());
  const [rooms, setRooms] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [products, setProducts] = useState([]);
  const [latestWin, setLatestWin] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMarketplace = useCallback(async () => {
    setLoading(true);
    const [activeResult, upcomingResult, productResult, closedResult] = await Promise.all([
      apiClient.get('/api/auction-rooms?status=active&limit=6').catch(() => null),
      apiClient.get('/api/auction-rooms/upcoming/scheduled').catch(() => null),
      apiClient.get('/api/products/recent?limit=4').catch(() => null),
      apiClient.get('/api/auction-rooms?status=closed&limit=12').catch(() => null),
    ]);
    setRooms(activeResult?.success ? (activeResult.data || []).slice(0, 3) : []);
    setUpcoming(upcomingResult?.success ? (upcomingResult.data || []).slice(0, 3) : []);
    setProducts(productResult?.success ? (productResult.products || []).slice(0, 4) : []);
    // Most recently ENDED room that actually has a winner. The endpoint sorts by
    // createdAt, so re-sort by endTime here rather than trusting the order.
    const won = (closedResult?.success ? closedResult.data || [] : [])
      .filter((room) => room?.currentHighestBid?.amount > 0)
      .sort((a, b) => new Date(b.endTime || 0) - new Date(a.endTime || 0));
    setLatestWin(won[0] || null);
    setLoading(false);
  }, []);

  useEffect(() => { loadMarketplace(); }, [loadMarketplace]);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const heroRoom = rooms[0] || upcoming[0];
  const heroImage = getImage(heroRoom);
  const winImage = getImage(latestWin);
  const winRetail = Number(latestWin?.productId?.price) || 0;
  const winPaid = Number(latestWin?.currentHighestBid?.amount) || 0;
  const winSaved = winRetail > winPaid && winRetail > 0
    ? Math.round(((winRetail - winPaid) / winRetail) * 100)
    : 0;
  const displayProducts = useMemo(
    () => products.length ? products : Array.from({ length: 4 }, (_, index) => ({ _id: null, name: copy.noProducts, category: copy.placeholderCategories[index], price: 0 })),
    [products, copy],
  );

  return (
    <div
      className={`vitria-landing ${theme.palette.mode === 'dark' ? 'is-dark' : 'is-light'}${copy.direction === 'rtl' ? ' is-rtl' : ''}`}
      dir={copy.direction}
    >
      <Navbar />

      <main>
        <section className="vl-hero">
          <div className="vl-hero-art" aria-hidden="true" />
          <div className="vl-hero-grid" aria-hidden="true" />
          <div className="vl-hero-glow" aria-hidden="true" />
          <div className="vl-shell vl-hero-inner">
            <div className="vl-hero-copy">
              <div className="vl-eyebrow"><span><BoltRoundedIcon /></span>{copy.eyebrow}</div>
              <h1>
                <span>{copy.titleA}</span>
                <span className="vl-gradient-text">{copy.titleB}</span>
                <span>{copy.titleC}</span>
              </h1>
              <p>{copy.heroBody}</p>
              <div className="vl-hero-actions">
                <RouterLink className="vl-button vl-button-primary" to="/rooms/bid">
                  <GavelRoundedIcon /><span>{copy.liveCta}</span><ArrowOutwardRoundedIcon />
                </RouterLink>
                <RouterLink className="vl-button vl-button-ghost" to="/ecommerce">
                  <span>{copy.marketCta}</span><EastRoundedIcon />
                </RouterLink>
              </div>
              <div className="vl-trust-row">
                <span><ShieldRoundedIcon />{copy.trustEscrow}</span>
                <span><BoltRoundedIcon />{copy.trustRealtime}</span>
                <span><VerifiedRoundedIcon />{copy.trustVerified}</span>
              </div>
            </div>

            <div className="vl-hero-live-card">
              <div className="vl-hero-live-head">
                <span className={latestWin ? 'is-win' : ''}>
                  {latestWin ? <EmojiEventsRoundedIcon /> : <i />}
                  {latestWin ? copy.latestWin : copy.nextDrop}
                </span>
                <strong>{latestWin ? timeAgo(latestWin.endTime, now, copy) : getCountdown(heroRoom?.endTime || heroRoom?.startTime, now)}</strong>
              </div>

              <div className={`vl-hero-live-body${(latestWin ? winImage : heroImage) ? '' : ' is-textonly'}`}>
                {latestWin
                  ? (winImage && <img src={winImage} alt="" />)
                  : (heroImage && <img src={heroImage} alt="" />)}
                <div>
                  <small>{translateCategory((latestWin || heroRoom)?.productId?.category, copy, copy.liveSelection)}</small>
                  <h2>{(latestWin || heroRoom)?.productId?.name || (latestWin || heroRoom)?.title || (latestWin ? copy.noWinYet : copy.noRoom)}</h2>
                  <p>
                    {latestWin
                      ? `${copy.wonBy} ${latestWin?.currentHighestBid?.userId?.username || '—'}`
                      : (heroRoom ? `${copy.startingAt} ${money(heroRoom?.currentHighestBid?.amount || heroRoom?.minBid, copy.locale)}` : copy.noRoomBody)}
                  </p>
                </div>
              </div>

              {latestWin ? (
                <div className="vl-hero-win-stats">
                  <div className="vl-win-stat">
                    <small>{copy.retailPrice}</small>
                    <strong className="vl-win-retail">{money(winRetail, copy.locale)}</strong>
                  </div>
                  <div className="vl-win-stat vl-win-stat-paid">
                    <small>{copy.winningPrice}</small>
                    <strong className="vl-win-paid">{money(winPaid, copy.locale)}</strong>
                  </div>
                  {winSaved > 0 && (
                    <span className="vl-win-badge">−{winSaved}% {copy.savedLabel}</span>
                  )}
                </div>
              ) : (
                <p className="vl-hero-live-empty">{copy.noWinBody}</p>
              )}

              <ArrowLink to={latestWin?._id ? `/auction-room/${latestWin._id}` : (heroRoom?._id ? `/auction-room/${heroRoom._id}` : '/rooms/bid')} light>{copy.openRoom}</ArrowLink>
            </div>
          </div>
          <div className="vl-scroll-cue"><span>{copy.scroll}</span><i /></div>
        </section>

        <div className="vl-marquee" aria-label={copy.marketplaceAria}>
          <div className="vl-marquee-track">
            {[...copy.marquee, ...copy.marquee].map((item, index) => (
              <React.Fragment key={`${item}-${index}`}><span>{item}</span><i>✦</i></React.Fragment>
            ))}
          </div>
        </div>

        <section className="vl-section vl-ecosystem">
          <div className="vl-shell">
            <header className="vl-section-head vl-section-head-wide">
              <div><span className="vl-kicker">{copy.ecosystemKicker}</span><h2>{copy.ecosystemTitle}</h2></div>
              <p>{copy.ecosystemBody}</p>
            </header>
            <div className="vl-ecosystem-grid">
              <RouterLink to="/rooms/bid" className="vl-mode-card vl-mode-auction">
                <span className="vl-mode-number">01</span><GavelRoundedIcon className="vl-mode-icon" />
                <div><h3>{copy.auctionTitle}</h3><p>{copy.auctionBody}</p><strong>{copy.explore}<ArrowOutwardRoundedIcon /></strong></div>
              </RouterLink>
              <RouterLink to="/ecommerce" className="vl-mode-card vl-mode-shop">
                <span className="vl-mode-number">02</span><StorefrontRoundedIcon className="vl-mode-icon" />
                <div><h3>{copy.shopTitle}</h3><p>{copy.shopBody}</p><strong>{copy.explore}<ArrowOutwardRoundedIcon /></strong></div>
              </RouterLink>
              <RouterLink to="/rooms/second-hand" className="vl-mode-card vl-mode-resale">
                <span className="vl-mode-number">03</span><RecyclingRoundedIcon className="vl-mode-icon" />
                <div><h3>{copy.resaleTitle}</h3><p>{copy.resaleBody}</p><strong>{copy.explore}<ArrowOutwardRoundedIcon /></strong></div>
              </RouterLink>
            </div>
          </div>
        </section>

        <section className="vl-section vl-live-section">
          <div className="vl-shell">
            <header className="vl-section-head">
              <div><span className="vl-kicker"><i className="vl-live-dot" />{copy.liveKicker}</span><h2>{copy.liveTitle}</h2><p>{copy.liveBody}</p></div>
              <ArrowLink to="/rooms/bid">{copy.seeAll}</ArrowLink>
            </header>
            {loading ? (
              <div className="vl-auction-grid">{[0, 1, 2].map((item) => <div className="vl-auction-skeleton" key={item} />)}</div>
            ) : rooms.length ? (
              <div className="vl-auction-grid">{rooms.map((room) => <AuctionTile room={room} copy={copy} now={now} key={room._id} />)}</div>
            ) : (
              <div className="vl-empty-live">
                <div className="vl-empty-orbit"><span><GavelRoundedIcon /></span></div>
                <div><span className="vl-kicker">00 : 00 : 00</span><h3>{copy.emptyTitle}</h3><p>{copy.emptyBody}</p><ArrowLink to="/ecommerce">{copy.browseShop}</ArrowLink></div>
              </div>
            )}
          </div>
        </section>

        <section className="vl-section vl-recovery-section">
          <div className="vl-shell vl-recovery-grid">
            <div className="vl-recovery-visual">
              <div className="vl-wallet-card">
                <div className="vl-wallet-top"><span><ReplayRoundedIcon /></span><small>{copy.recoveryLabel}</small><b>•• 2471</b></div>
                <div className="vl-wallet-amount"><small>{copy.recovered}</small><strong>+ 38.00 <em>TND</em></strong></div>
                <div className="vl-wallet-status"><i /><span>{copy.usableOn}</span><ShieldRoundedIcon /></div>
              </div>
              <div className="vl-recovery-ring ring-one" /><div className="vl-recovery-ring ring-two" />
              <span className="vl-floating-token token-one">V</span><span className="vl-floating-token token-two">+</span>
            </div>
            <div className="vl-recovery-copy">
              <span className="vl-kicker">{copy.recoveryKicker}</span>
              <h2>{copy.recoveryTitle}</h2><p>{copy.recoveryBody}</p>
              <ul>
                <li><ReplayRoundedIcon /><span><strong>{copy.recoveryValueTitle}</strong><small>{copy.recoveryValueBody}</small></span></li>
                <li><PaymentsRoundedIcon /><span><strong>{copy.flexibleTitle}</strong><small>{copy.flexibleBody}</small></span></li>
                <li><LockRoundedIcon /><span><strong>{copy.visibleTitle}</strong><small>{copy.visibleBody}</small></span></li>
              </ul>
              <ArrowLink to={user ? '/recovery-wallet' : '/auth'}>{copy.learnWallet}</ArrowLink>
            </div>
          </div>
        </section>

        <section className="vl-section vl-market-section">
          <div className="vl-shell">
            <header className="vl-section-head">
              <div><span className="vl-kicker">{copy.marketKicker}</span><h2>{copy.marketTitle}</h2><p>{copy.marketBody}</p></div>
              <ArrowLink to="/ecommerce">{copy.marketCta}</ArrowLink>
            </header>
            <div className="vl-product-grid">{displayProducts.map((product, index) => <ProductTile product={product} copy={copy} index={index} key={product._id || index} />)}</div>
          </div>
        </section>

        <section className="vl-section vl-how-section">
          <div className="vl-shell">
            <header className="vl-section-head vl-section-head-centered"><span className="vl-kicker">{copy.howKicker}</span><h2>{copy.howTitle}</h2></header>
            <div className="vl-steps">
              <article><span>01</span><div className="vl-step-icon"><AutoAwesomeRoundedIcon /></div><h3>{copy.stepOne}</h3><p>{copy.stepOneBody}</p></article>
              <article><span>02</span><div className="vl-step-icon"><TrendingUpRoundedIcon /></div><h3>{copy.stepTwo}</h3><p>{copy.stepTwoBody}</p></article>
              <article><span>03</span><div className="vl-step-icon"><EmojiEventsRoundedIcon /></div><h3>{copy.stepThree}</h3><p>{copy.stepThreeBody}</p></article>
            </div>
          </div>
        </section>

        <section className="vl-seller-section">
          <div className="vl-shell vl-seller-inner">
            <div className="vl-seller-copy"><span className="vl-kicker">{copy.sellerKicker}</span><h2>{copy.sellerTitle}</h2><p>{copy.sellerBody}</p></div>
            <div className="vl-seller-actions">
              <RouterLink className="vl-button vl-button-light" to={user ? '/rooms/second-hand' : '/auth'}><span>{copy.sellCta}</span><ArrowOutwardRoundedIcon /></RouterLink>
              {!user && <RouterLink className="vl-button vl-button-outline-light" to="/auth">{copy.createCta}</RouterLink>}
            </div>
            <RecyclingRoundedIcon className="vl-seller-watermark" />
          </div>
        </section>

        <section className="vl-final-section">
          <div className="vl-final-glow" />
          <div className="vl-shell vl-final-inner">
            <span>{copy.finalBadge}</span><h2>{copy.finalTitle}</h2><p>{copy.finalBody}</p>
            <RouterLink className="vl-button vl-button-primary" to={user ? '/rooms/bid' : '/auth'}><span>{user ? copy.liveCta : copy.joinCta}</span><ArrowOutwardRoundedIcon /></RouterLink>
          </div>
        </section>
      </main>

      <footer className="vl-footer">
        <div className="vl-shell vl-footer-grid">
          <div className="vl-footer-brand"><img src="/logo192.png" alt="Vitria" /><div><strong>VITRIA</strong><span>{copy.brandTagline}</span></div></div>
          <div><strong>{copy.footerMarketplace}</strong><RouterLink to="/rooms/bid">{copy.footerLiveAuctions}</RouterLink><RouterLink to="/ecommerce">{copy.footerShop}</RouterLink><RouterLink to="/rooms/second-hand">{copy.footerSecondHand}</RouterLink></div>
          <div><strong>{copy.footerAccount}</strong><RouterLink to={user ? '/profile' : '/auth'}>{copy.footerProfile}</RouterLink><RouterLink to={user ? '/history' : '/auth'}>{copy.footerHistory}</RouterLink><RouterLink to={user ? '/settings' : '/auth'}>{copy.footerSettings}</RouterLink></div>
          <div><strong>{copy.footerRegion}</strong><a href="mailto:contact@vitria.tn">contact@vitria.tn</a><span>{copy.footerLocation}</span><span>© {new Date().getFullYear()} {copy.footerCopyright}</span></div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
